<script setup lang="ts">
/**
 * A text field (or text area with `multiline`) for templates with
 * `{{placeholder}}`s — mail subjects, notification texts, expressions. Typing
 * `{{` opens the list of `suggestions` under the field and filters it by what
 * follows; ↑/↓ pick, Enter or Tab insert `{{key}}` at the caret, Escape
 * closes. The `{ }` button opens the same list with a search field, for
 * browsing without knowing the key.
 *
 * `v-model` is the raw text; the field never rewrites what the user typed
 * apart from the inserted placeholder. Suggestions are grouped by `group`.
 */
import Braces from 'lucide-vue-next/dist/esm/icons/braces.js'; import Search from 'lucide-vue-next/dist/esm/icons/search.js';
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue';
import { NxIconButton } from '../IconButton';
import { useFloatingPanel } from '../Popover';
import {
  filterSuggestions,
  insertPlaceholder,
  openPlaceholderAt,
  type NxTemplateSuggestion,
} from './placeholders';

const props = withDefaults(
  defineProps<{
    suggestions: readonly NxTemplateSuggestion[];
    multiline?: boolean;
    rows?: number;
    label?: string;
    hint?: string;
    placeholder?: string;
    disabled?: boolean;
    insertLabel?: string;
    searchLabel?: string;
    emptyLabel?: string;
  }>(),
  {
    multiline: false,
    rows: 4,
    label: undefined,
    hint: undefined,
    placeholder: undefined,
    disabled: false,
    insertLabel: 'Platzhalter einfügen',
    searchLabel: 'Platzhalter suchen',
    emptyLabel: 'Kein Platzhalter gefunden',
  }
);

const model = defineModel<string>({ default: '' });

const id = useId();
const listId = `${id}-list`;
const hintId = `${id}-hint`;

const fieldRef = useTemplateRef<HTMLInputElement | HTMLTextAreaElement>('field');
const wrapRef = useTemplateRef<HTMLElement>('wrap');
const panelRef = useTemplateRef<HTMLElement>('panel');
const searchRef = useTemplateRef<HTMLInputElement>('searchField');

/** `inline`: opened by typing `{{` (focus stays in the field); `browse`: by the button. */
const mode = ref<'inline' | 'browse' | null>(null);
const open = computed(() => mode.value !== null);
const search = ref('');
/** Where the open `{{partial` starts (inline mode). */
const openFrom = ref(0);
const active = ref(0);

const { floatingStyles } = useFloatingPanel(wrapRef, panelRef, {
  open,
  placement: 'bottom-start',
  offset: 4,
});

const matches = computed(() => filterSuggestions(props.suggestions, search.value));
const grouped = computed(() => {
  const out: { group: string; items: { item: NxTemplateSuggestion; index: number }[] }[] = [];
  matches.value.forEach((item, index) => {
    const group = item.group ?? '';
    let bucket = out.find((g) => g.group === group);
    if (!bucket) {
      bucket = { group, items: [] };
      out.push(bucket);
    }
    bucket.items.push({ item, index });
  });
  return out;
});
const optionId = (index: number) => `${id}-opt-${index}`;
/** The placeholder as it lands in the text (kept out of the template's mustaches). */
const tokenOf = (key: string) => `{{${key}}}`;
const activeId = computed(() =>
  open.value && matches.value.length ? optionId(active.value) : undefined
);

watch(matches, () => {
  active.value = 0;
});

watch(open, async (isOpen) => {
  await nextTick();
  const panel = panelRef.value;
  if (!panel) return;
  if (isOpen) {
    panel.showPopover?.();
    if (mode.value === 'browse') searchRef.value?.focus();
  } else {
    panel.hidePopover?.();
  }
});

function close(refocus = false) {
  mode.value = null;
  search.value = '';
  if (refocus) fieldRef.value?.focus();
}

function openBrowse() {
  if (props.disabled) return;
  if (mode.value === 'browse') {
    close(true);
    return;
  }
  search.value = '';
  mode.value = 'browse';
}

/** Re-reads the caret: opens, filters or closes the inline list. */
function syncInline() {
  const el = fieldRef.value;
  if (!el || mode.value === 'browse') return;
  const caret = el.selectionStart ?? model.value.length;
  const hit = openPlaceholderAt(model.value, caret);
  if (!hit) {
    if (mode.value === 'inline') close();
    return;
  }
  openFrom.value = hit.from;
  search.value = hit.query;
  mode.value = 'inline';
}

function onInput(event: Event) {
  model.value = (event.target as HTMLInputElement).value;
  nextTick(syncInline);
}

function choose(item: NxTemplateSuggestion) {
  const el = fieldRef.value;
  const text = model.value;
  const caret = el?.selectionStart ?? text.length;
  const end = el?.selectionEnd ?? caret;
  const start = mode.value === 'inline' ? openFrom.value : Math.min(caret, end);
  const next = insertPlaceholder(text, start, Math.max(caret, end), item.key);
  model.value = next.text;
  close();
  nextTick(() => {
    const field = fieldRef.value;
    if (!field) return;
    field.focus();
    field.setSelectionRange?.(next.caret, next.caret);
  });
}

/** Arrow, Enter, Tab and Escape while the list is open — from the field or the search. */
function onListKeys(event: KeyboardEvent) {
  if (!open.value) return;
  const n = matches.value.length;
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      if (n) active.value = (active.value + 1) % n;
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (n) active.value = (active.value - 1 + n) % n;
      break;
    case 'Enter':
    case 'Tab': {
      const item = matches.value[active.value];
      if (!item) return;
      event.preventDefault();
      choose(item);
      break;
    }
    case 'Escape':
      event.preventDefault();
      event.stopPropagation();
      close(mode.value === 'browse');
      break;
  }
}

function onFieldKeydown(event: KeyboardEvent) {
  if (mode.value === 'inline') onListKeys(event);
}

function onOutside(event: PointerEvent) {
  const target = event.target as Node;
  if (wrapRef.value?.contains(target) || panelRef.value?.contains(target)) return;
  close();
}
watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onOutside, true);
  else document.removeEventListener('pointerdown', onOutside, true);
});
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside, true));
</script>

<template>
  <div class="nx-template-input" :class="{ 'is-disabled': props.disabled }">
    <label v-if="props.label" class="nx-template-input__label" :for="id">{{ props.label }}</label>
    <div ref="wrap" class="nx-template-input__wrap" :class="{ 'is-multiline': props.multiline }">
      <component
        :is="props.multiline ? 'textarea' : 'input'"
        :id="id"
        ref="field"
        class="nx-template-input__field"
        :value="model"
        :rows="props.multiline ? props.rows : undefined"
        :type="props.multiline ? undefined : 'text'"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        role="combobox"
        :aria-expanded="mode === 'inline'"
        :aria-controls="listId"
        aria-autocomplete="list"
        :aria-activedescendant="mode === 'inline' ? activeId : undefined"
        :aria-describedby="props.hint ? hintId : undefined"
        spellcheck="false"
        data-testid="nx-template-field"
        @input="onInput"
        @keydown="onFieldKeydown"
        @click="syncInline"
      />
      <NxIconButton
        class="nx-template-input__insert"
        height="xs"
        :title="props.insertLabel"
        :aria-label="props.insertLabel"
        :aria-expanded="mode === 'browse'"
        :aria-controls="listId"
        :disabled="props.disabled"
        data-testid="nx-template-insert"
        @click="openBrowse"
      >
        <Braces class="nxt-icon-sm" aria-hidden="true" />
      </NxIconButton>
    </div>
    <p v-if="props.hint" :id="hintId" class="nx-template-input__hint">{{ props.hint }}</p>

    <div
      ref="panel"
      class="nx-template-input__panel"
      popover="manual"
      :style="floatingStyles"
      data-testid="nx-template-panel"
    >
      <template v-if="open">
        <div v-if="mode === 'browse'" class="nx-template-input__search">
          <Search class="nxt-icon-sm" aria-hidden="true" />
          <input
            ref="searchField"
            v-model="search"
            type="search"
            :placeholder="props.searchLabel"
            :aria-label="props.searchLabel"
            role="combobox"
            aria-expanded="true"
            :aria-controls="listId"
            aria-autocomplete="list"
            :aria-activedescendant="activeId"
            data-testid="nx-template-search"
            @keydown="onListKeys"
          />
        </div>
        <ul
          :id="listId"
          class="nx-template-input__list"
          role="listbox"
          :aria-label="props.insertLabel"
        >
          <template v-for="bucket in grouped" :key="bucket.group">
            <li v-if="bucket.group" class="nx-template-input__group" role="presentation">
              {{ bucket.group }}
            </li>
            <li
              v-for="{ item, index } in bucket.items"
              :id="optionId(index)"
              :key="item.key"
              class="nx-template-input__option"
              :class="{ 'is-active': index === active }"
              role="option"
              :aria-selected="index === active"
              data-testid="nx-template-option"
              @pointerdown.prevent
              @pointerenter="active = index"
              @click="choose(item)"
            >
              <span class="nx-template-input__option-main">
                <span class="nx-template-input__option-label">{{ item.label }}</span>
                <code class="nx-template-input__option-key" v-text="tokenOf(item.key)" />
              </span>
              <span v-if="item.description" class="nx-template-input__option-desc">
                {{ item.description }}
              </span>
            </li>
          </template>
          <li v-if="!matches.length" class="nx-template-input__empty" role="presentation">
            {{ props.emptyLabel }}
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<style scoped>
.nx-template-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-template-input__label {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-template-input__wrap {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  padding-inline-end: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-template-input__wrap.is-multiline {
  align-items: flex-start;
  padding-block-start: var(--nxt-space-2xs);
}
.nx-template-input__wrap:focus-within {
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-template-input__field {
  flex: 1 1 auto;
  min-width: 0;
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: 0;
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  outline: none;
  resize: vertical;
}
.nx-template-input__wrap.is-multiline .nx-template-input__field {
  padding-block-start: var(--nxt-space-2xs);
  line-height: var(--nxt-line-height-normal);
}
.nx-template-input.is-disabled .nx-template-input__wrap {
  opacity: 0.6;
}
.nx-template-input__hint {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-template-input__panel {
  inset: auto;
  width: min(var(--nxt-panel-width-lg), 90vw);
  max-height: calc(var(--nxt-size-unit) * 20);
  margin: 0;
  padding: 0;
  overflow: auto;
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-popover-bg);
  color: var(--nxt-popover-fg);
  box-shadow: var(--nxt-popover-shadow);
}
.nx-template-input__panel:not(:popover-open) {
  display: none;
}
.nx-template-input__search {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-popover-bg);
  color: var(--nxt-fg-muted);
}
.nx-template-input__search input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  outline: none;
}
.nx-template-input__list {
  margin: 0;
  padding: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
  list-style: none;
}
.nx-template-input__group {
  padding: var(--nxt-space-xs) var(--nxt-space-xs) var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.nx-template-input__option {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  cursor: pointer;
}
.nx-template-input__option.is-active {
  background: var(--nxt-brand-subtle-bg);
}
.nx-template-input__option-main {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-template-input__option-label {
  overflow: hidden;
  color: var(--nxt-fg);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-template-input__option-key {
  flex: 0 0 auto;
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-hue-violet-subtle-bg);
  color: var(--nxt-hue-violet-fg);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: var(--nxt-font-size-xs);
}
.nx-template-input__option-desc {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-template-input__empty {
  padding: var(--nxt-space-sm);
  color: var(--nxt-fg-muted);
  text-align: center;
}
@media (prefers-reduced-motion: reduce) {
  .nx-template-input__wrap {
    transition: none;
  }
}
</style>
